<template>
  <div class="checklist-section">
    <!-- フィルターセクション -->
    <div class="action-bar">
      <div v-if="canWrite && itemType === 'BELONGINGS'" class="template-actions">
        <button
          v-if="!isTrialMode"
          class="template-edit-btn"
          @click="openTemplateEditor"
        >
          テンプレートを編集
        </button>
        <button
          class="template-edit-btn template-create-btn"
          @click="copyFromTemplate"
          :disabled="isCopyingTemplate"
        >
          {{
            isCopyingTemplate
              ? "自動作成中..."
              : "テンプレートから自動作成"
          }}
        </button>
        <p v-if="isTrialMode" class="template-login-note">
          ログインすると持ち物テンプレートを編集できます。
        </p>
      </div>

      <!-- モバイル: フィルタートグルボタン -->
      <button
        class="filter-toggle-btn"
        @click="filterSidebarOpen = !filterSidebarOpen"
      >
        <IconSvg name="list-solid-full" size="sm" />
        フィルター
        <span v-if="hasFilters" class="filter-active-dot"></span>
      </button>

      <!-- フィルターオーバーレイ（モバイル） -->
      <div
        v-if="filterSidebarOpen"
        class="filter-overlay"
        @click="filterSidebarOpen = false"
      ></div>

      <!-- フィルターサイドバー -->
      <div :class="['filter-sidebar', { 'is-open': filterSidebarOpen }]">
        <div class="filter-sidebar-header">
          <span>フィルター</span>
          <button class="sidebar-close-btn" @click="filterSidebarOpen = false">
            ×
          </button>
        </div>
        <!-- フィルターバー -->
        <div class="filter-bar">
          <div class="filter-group">
            <label>状態:</label>
            <select v-model="filters.completedFlg">
              <option value="">すべて</option>
              <option value="0">未完了</option>
              <option value="1">完了済み</option>
            </select>
          </div>

          <div v-if="itemType === 'TODO'" class="filter-group filter-group-date">
            <label>期限:</label>
            <div class="date-range-inputs">
              <input type="date" v-model="filters.deadlineFrom" />
              <span class="date-range-sep">〜</span>
              <input type="date" v-model="filters.deadlineTo" />
            </div>
          </div>

          <div class="filter-group">
            <label>カテゴリ:</label>
            <select v-model="filters.categoryName">
              <option value="">すべて</option>
              <option v-for="cat in categories" :key="cat" :value="cat">
                {{ cat }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>{{ itemType === "BELONGINGS" ? "持って行く人:" : "担当者:" }}</label>
            <select v-model="filters.assignedMemberCode">
              <option value="">すべて</option>
              <option
                v-for="member in shioriMembers"
                :key="member.MEMBER_CODE"
                :value="member.MEMBER_CODE"
              >
                {{ member.MEMBER_NAME }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>並び替え:</label>
            <select v-model="filters.sortBy">
              <option value="created_at">作成日</option>
              <option v-if="itemType === 'TODO'" value="deadline">期限</option>
              <option v-if="itemType === 'TODO'" value="priority">
                優先度
              </option>
            </select>
          </div>

          <button
            class="filter-reset-btn"
            @click="resetFilters"
            title="フィルターをリセット"
          >
            ⟲ リセット
          </button>
        </div>
      </div>
    </div>

    <!-- 一括操作ツールバー -->
    <div v-if="canWrite && selectedItems.length > 0" class="bulk-action-bar">
      <span class="bulk-count">{{ selectedItems.length }}件選択中</span>
      <button class="bulk-btn bulk-complete" @click="handleBulkComplete">
        一括完了
      </button>
      <button class="bulk-btn bulk-delete" @click="handleBulkDelete">
        一括削除
      </button>
      <button class="bulk-btn bulk-cancel" @click="clearSelection">
        選択解除
      </button>
    </div>

    <!-- ローディング表示 -->
    <div v-if="checklistStore.loading && !hasEverLoaded" class="loading">
      <div class="spinner"></div>
      読み込み中...
    </div>

    <!-- エラー表示 -->
    <div v-else-if="checklistStore.error" class="error-message">
      {{ checklistStore.error }}
    </div>

    <!-- チェックリストがない場合 -->
    <div v-else-if="filteredChecklists.length === 0" class="empty">
      <div class="empty-icon">
        <IconSvg
          :name="
            itemType === 'BELONGINGS'
              ? 'bag-shopping-solid-full'
              : 'check-solid-full'
          "
          size="xl"
        />
      </div>
      <p>
        {{
          hasFilters
            ? "フィルター条件に一致するチェックリストがありません"
            : itemType === "BELONGINGS"
            ? "持ち物リストがまだありません"
            : "To Doがまだありません"
        }}
      </p>
      <button v-if="canWrite" class="create-btn" @click="openCreateModal">
        {{
          itemType === "BELONGINGS" ? "最初の持ち物を追加" : "最初のTo Doを追加"
        }}
      </button>
      <button
        v-if="canWrite && itemType === 'BELONGINGS'"
        class="template-copy-btn"
        @click="copyFromTemplate"
        :disabled="isCopyingTemplate"
      >
        {{
          isCopyingTemplate
            ? "自動作成中..."
            : "テンプレートから自動作成"
        }}
      </button>
      <p
        v-if="itemType === 'BELONGINGS' && isTrialMode"
        class="template-login-note empty-note"
      >
        ログインすると持ち物テンプレートを編集できます。
      </p>
    </div>

    <!-- チェックリスト表示 -->
    <div v-else class="checklist-content">
      <ChecklistCategory
        v-for="(items, categoryName) in groupedChecklist"
        :key="categoryName"
        :title="categoryName"
        :items="items"
        :shiori-id="shioriId"
        :item-type="itemType"
        :shiori-members="shioriMembers"
        :selected-items="selectedItems"
        :can-write="canWrite"
        @toggle-complete="handleToggleComplete"
        @edit="handleEdit"
        @copy="handleCopy"
        @delete="handleDelete"
        @reload="loadChecklists"
        @select-item="handleSelectItem"
      />
    </div>

    <!-- フローティングボタン（チェックリスト作成） -->
    <button
      v-if="canWrite && !isModalOpen"
      class="floating-btn"
      @click="openCreateModal"
      :title="itemType === 'BELONGINGS' ? '持ち物追加' : 'To Do追加'"
    >
      <span class="floating-btn-icon">
        <IconSvg name="plus-solid-full" size="md" />
      </span>
    </button>

    <!-- チェックリスト作成/編集モーダル -->
    <CreateChecklistModal
      v-if="canWrite"
      :is-open="isModalOpen"
      :shiori-id="shioriId"
      :edit-mode="editMode"
      :copy-mode="copyMode"
      :checklist-data="editingChecklist"
      :item-type="itemType"
      @close="closeModal"
      @create="handleCreate"
      @update="handleUpdate"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import ChecklistCategory from "@/components/ChecklistCategory.vue";
import CreateChecklistModal from "@/components/CreateChecklistModal.vue";
import IconSvg from "@/components/IconSvg.vue";
import { useChecklistStore } from "@/store/checklist-store";
import { useShioriMemberStore } from "@/store/shiori-member-store";
import { useChecklistTemplateStore } from "@/store/checklist-template-store";

export default {
  name: "ChecklistSection",
  components: {
    ChecklistCategory,
    CreateChecklistModal,
    IconSvg,
  },
  props: {
    itemType: {
      type: String,
      default: "TODO",
    },
    canWrite: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const route = useRoute();
    const checklistStore = useChecklistStore();
    const shioriMemberStore = useShioriMemberStore();
    const checklistTemplateStore = useChecklistTemplateStore();
    const isCopyingTemplate = ref(false);
    const hasEverLoaded = ref(false);

    const isModalOpen = ref(false);
    const editMode = ref(false);
    const copyMode = ref(false);
    const editingChecklist = ref(null);
    const filterSidebarOpen = ref(false);
    const selectedItems = ref([]);

    const shioriId = computed(() => route.params.id);
    const isTrialMode = computed(() => route.name === "Trial");

    // フィルター状態
    const filters = ref({
      completedFlg: "",
      deadlineFrom: "",
      deadlineTo: "",
      categoryName: "",
      assignedMemberCode: "",
      sortBy: "created_at",
    });

    // しおりメンバー取得
    const shioriMembers = computed(() => {
      return shioriMemberStore.members[shioriId.value] || [];
    });

    // チェックリスト取得
    const checklists = computed(() => {
      return (
        checklistStore.getChecklistsBySioriId(shioriId.value, props.itemType) ||
        []
      );
    });

    // カテゴリ一覧（重複なし）
    const categories = computed(() => {
      const cats = new Set();
      checklists.value.forEach((item) => {
        if (item.CATEGORY_NAME) {
          cats.add(item.CATEGORY_NAME);
        }
      });
      return Array.from(cats).sort();
    });

    // フィルター適用済みチェックリスト
    const filteredChecklists = computed(() => {
      let result = checklists.value.filter(
        (item) => (item.ITEM_TYPE || "TODO") === props.itemType,
      );

      // 完了状態フィルター
      if (filters.value.completedFlg) {
        result = result.filter(
          (item) => item.COMPLETED_FLG === filters.value.completedFlg,
        );
      }

      // 期限フィルター（日付範囲）
      if (
        props.itemType === "TODO" &&
        (filters.value.deadlineFrom || filters.value.deadlineTo)
      ) {
        result = result.filter((item) => {
          if (!item.DEADLINE) return false;
          const deadline = item.DEADLINE.slice(0, 10);
          if (
            filters.value.deadlineFrom &&
            deadline < filters.value.deadlineFrom
          )
            return false;
          if (filters.value.deadlineTo && deadline > filters.value.deadlineTo)
            return false;
          return true;
        });
      }

      // カテゴリフィルター
      if (filters.value.categoryName) {
        result = result.filter(
          (item) => item.CATEGORY_NAME === filters.value.categoryName,
        );
      }

      // 担当者フィルター
      if (filters.value.assignedMemberCode) {
        result = result.filter(
          (item) =>
            item.ASSIGNED_MEMBER_CODE === filters.value.assignedMemberCode ||
            (item.MEMBERS || []).some(
              (member) =>
                member.ASSIGNEE_USER_CODE ===
                filters.value.assignedMemberCode,
            ),
        );
      }

      return result;
    });

    // カテゴリごとにグループ化
    const groupedChecklist = computed(() => {
      const grouped = {};
      filteredChecklists.value.forEach((item) => {
        const category = item.CATEGORY_NAME || "その他";
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(item);
      });
      return grouped;
    });

    // フィルターが適用されているか
    const hasFilters = computed(() => {
      return (
        filters.value.completedFlg ||
        (props.itemType === "TODO" &&
          (filters.value.deadlineFrom || filters.value.deadlineTo)) ||
        filters.value.categoryName ||
        filters.value.assignedMemberCode
      );
    });

    const openCreateModal = () => {
      if (!props.canWrite) return;
      editMode.value = false;
      copyMode.value = false;
      editingChecklist.value = null;
      isModalOpen.value = true;
    };

    const openTemplateEditor = () => {
      if (!props.canWrite) return;
      window.dispatchEvent(
        new CustomEvent("open-settings", {
          detail: { view: "checklist-template" },
        }),
      );
    };

    const closeModal = () => {
      isModalOpen.value = false;
      setTimeout(() => {
        editMode.value = false;
        copyMode.value = false;
        editingChecklist.value = null;
      }, 300);
    };

    const handleCreate = async () => {
      if (!props.canWrite) return;
      await loadChecklists();
      closeModal();
    };

    const handleUpdate = async (payload) => {
      if (!props.canWrite) return;
      if (payload) {
        checklistStore.patchChecklistLocal(
          shioriId.value,
          props.itemType,
          payload.pageNo,
          payload.checklistNo,
          payload.updateData,
          shioriMembers.value,
        );
      } else {
        await loadChecklists();
      }
      closeModal();
    };

    const handleToggleComplete = async (item) => {
      if (!props.canWrite) return;
      const savedScrollY = window.scrollY;
      try {
        await checklistStore.toggleComplete(
          shioriId.value,
          item.PAGE_NO,
          item.CHECKLIST_NO,
          item.COMPLETED_FLG,
        );
        await loadChecklists();
        await nextTick();
        window.scrollTo(0, savedScrollY);
      } catch (error) {
        console.error("完了状態の切り替えに失敗しました:", error);
      }
    };

    const handleSelectItem = (item) => {
      if (!props.canWrite) return;
      const key = `${item.PAGE_NO}-${item.CHECKLIST_NO}`;
      const idx = selectedItems.value.indexOf(key);
      if (idx === -1) {
        selectedItems.value.push(key);
      } else {
        selectedItems.value.splice(idx, 1);
      }
    };

    const clearSelection = () => {
      selectedItems.value = [];
    };

    const handleBulkComplete = async () => {
      if (!props.canWrite) return;
      const allItems = Object.values(groupedChecklist.value).flat();
      const targets = allItems.filter((item) =>
        selectedItems.value.includes(`${item.PAGE_NO}-${item.CHECKLIST_NO}`),
      );
      for (const item of targets) {
        if (item.COMPLETED_FLG !== "1") {
          await checklistStore.toggleComplete(
            shioriId.value,
            item.PAGE_NO,
            item.CHECKLIST_NO,
            item.COMPLETED_FLG,
          );
        }
      }
      clearSelection();
      await loadChecklists();
    };

    const handleBulkDelete = async () => {
      if (!props.canWrite) return;
      if (
        !confirm(
          `${selectedItems.value.length}件のアイテムを削除してもよろしいですか?`,
        )
      )
        return;
      const allItems = Object.values(groupedChecklist.value).flat();
      const targets = allItems.filter((item) =>
        selectedItems.value.includes(`${item.PAGE_NO}-${item.CHECKLIST_NO}`),
      );
      try {
        await checklistStore.bulkDeleteChecklists(shioriId.value, targets);
        clearSelection();
        await loadChecklists();
      } catch (error) {
        console.error("一括削除に失敗しました:", error);
        alert(
          error.response?.data?.detail ||
            "チェックリストの一括削除に失敗しました",
        );
      }
    };

    const handleEdit = (item) => {
      if (!props.canWrite) return;
      editMode.value = true;
      copyMode.value = false;
      editingChecklist.value = item;
      isModalOpen.value = true;
    };

    const handleCopy = (item) => {
      if (!props.canWrite) return;
      editMode.value = false;
      copyMode.value = true;
      editingChecklist.value = {
        ITEM_NAME: `${item.ITEM_NAME}（コピー）`,
        MEMO_TEXT: item.MEMO_TEXT,
        CATEGORY_NAME: item.CATEGORY_NAME,
        ASSIGNED_MEMBER_CODE: item.ASSIGNED_MEMBER_CODE,
        DEADLINE: item.DEADLINE,
        PRIORITY_LEVEL: item.PRIORITY_LEVEL,
        DEADLINE_NOTIFY_FLG: item.DEADLINE_NOTIFY_FLG,
        MEMBERS: item.MEMBERS || [],
      };
      isModalOpen.value = true;
    };

    const handleDelete = async (item) => {
      if (!props.canWrite) return;
      if (!confirm(`「${item.ITEM_NAME}」を削除してもよろしいですか?`)) {
        return;
      }

      try {
        await checklistStore.deleteChecklist(
          shioriId.value,
          item.PAGE_NO,
          item.CHECKLIST_NO,
        );
        await loadChecklists();
      } catch (error) {
        console.error("削除に失敗しました:", error);
        alert("削除に失敗しました");
      }
    };

    const resetFilters = () => {
      filters.value = {
        completedFlg: "",
        deadlineFrom: "",
        deadlineTo: "",
        categoryName: "",
        assignedMemberCode: "",
        sortBy: "created_at",
      };
    };

    const loadChecklists = async () => {
      if (!shioriId.value) return;

      const filterParams = {
        item_type: props.itemType,
        sortBy: filters.value.sortBy || "created_at",
      };
      if (filters.value.completedFlg) {
        filterParams.completedFlg = filters.value.completedFlg;
      }
      if (filters.value.categoryName) {
        filterParams.categoryName = filters.value.categoryName;
      }
      if (filters.value.assignedMemberCode) {
        filterParams.assignedMemberCode = filters.value.assignedMemberCode;
      }
      if (props.itemType === "TODO" && filters.value.deadlineFrom) {
        filterParams.deadlineFrom = filters.value.deadlineFrom;
      }
      if (props.itemType === "TODO" && filters.value.deadlineTo) {
        filterParams.deadlineTo = filters.value.deadlineTo;
      }

      await checklistStore.fetchChecklists(shioriId.value, filterParams);
    };

    // 初回読み込み
    onMounted(async () => {
      if (shioriId.value) {
        await shioriMemberStore.fetchMembers(shioriId.value);
        await loadChecklists();
        hasEverLoaded.value = true;
      }
    });

    // フィルター変更時に再読み込み
    watch(
      filters,
      async () => {
        await loadChecklists();
      },
      { deep: true },
    );

    watch(
      () => props.itemType,
      async () => {
        selectedItems.value = [];
        filters.value.categoryName = "";
        if (props.itemType === "BELONGINGS") {
          if (filters.value.sortBy === "priority") {
            filters.value.sortBy = "created_at";
          }
        }
        await loadChecklists();
      },
    );

    return {
      shioriId,
      isTrialMode,
      isModalOpen,
      editMode,
      copyMode,
      editingChecklist,
      hasEverLoaded,
      checklistStore,
      filters,
      shioriMembers,
      categories,
      filteredChecklists,
      groupedChecklist,
      hasFilters,
      filterSidebarOpen,
      openCreateModal,
      openTemplateEditor,
      closeModal,
      handleCreate,
      handleUpdate,
      handleToggleComplete,
      handleEdit,
      handleCopy,
      handleDelete,
      resetFilters,
      selectedItems,
      handleSelectItem,
      clearSelection,
      handleBulkComplete,
      handleBulkDelete,
      isCopyingTemplate,
      copyFromTemplate: async () => {
        if (!props.canWrite) return;
        if (isCopyingTemplate.value) return;
        isCopyingTemplate.value = true;
        try {
          const result = await checklistTemplateStore.copyTemplateToChecklist(
            shioriId.value,
          );
          alert(result.message);
          await loadChecklists();
        } catch (e) {
          alert(
            e.response?.data?.detail ||
              "テンプレートからのコピーに失敗しました",
          );
        } finally {
          isCopyingTemplate.value = false;
        }
      },
    };
  },
};
</script>

<style scoped>
.checklist-section {
  position: relative;
  padding-bottom: 100px;
}

/* アクションバー */
.action-bar {
  margin-bottom: 1.5rem;
  position: relative;
}

.template-edit-btn {
  padding: 0.6rem 1rem;
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  background: white;
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
}

.template-edit-btn:hover {
  background: var(--primary-color);
  color: white;
}

.template-actions {
  display: flex;
  gap: 0.625rem;
  flex-wrap: wrap;
  align-items: center;
}

.template-create-btn {
  background: var(--primary-color);
  color: white;
}

.template-create-btn:disabled {
  opacity: 0.6;
  cursor: wait;
}

.template-login-note {
  flex-basis: 100%;
  margin: 0;
  color: #6b7280;
  font-size: 0.82rem;
  line-height: 1.5;
}

.template-login-note.empty-note {
  margin-top: 0.75rem;
}

/* フィルタートグルボタン (モバイル) */
.filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  cursor: pointer;
  position: relative;
  box-shadow: var(--shadow-sm);
}

.filter-active-dot {
  width: 8px;
  height: 8px;
  background: var(--primary-color);
  border-radius: 50%;
}

/* フィルターオーバーレイ */
.filter-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 200;
}

/* フィルターサイドバー */
.filter-sidebar {
  position: fixed;
  top: 0;
  left: -100%;
  width: min(320px, 85vw);
  height: 100dvh;
  background: white;
  z-index: 201;
  transition: left 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
}

.filter-sidebar.is-open {
  left: 0;
}

.filter-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 700;
  font-size: 1rem;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  flex-shrink: 0;
}

.sidebar-close-btn {
  width: 2rem;
  height: 2rem;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 8px;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* フィルターバー */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  flex: 1;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
}

.filter-group label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
}

.filter-group select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s;
}

.filter-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.2);
}

.filter-reset-btn {
  padding: 0.5rem 1rem;
  background-color: #e5e7eb;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.2s;
}

.filter-reset-btn:hover {
  background-color: #d1d5db;
}

.filter-group-date {
  min-width: 260px;
}

.date-range-inputs {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.date-range-inputs input[type="date"] {
  padding: 0.5rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s;
  width: 130px;
}

.date-range-inputs input[type="date"]:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.2);
}

.date-range-sep {
  color: #6b7280;
  font-size: 0.875rem;
  flex-shrink: 0;
}

/* ローディング */
.loading {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* エラー */
.error-message {
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

/* 空の状態 */
.empty {
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty p {
  margin-bottom: 1.5rem;
  font-size: 1rem;
}

.create-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-hover) 100%
  );
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.4);
}

/* チェックリストコンテンツ */
.checklist-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* 一括操作バー（タブリストの直下より上に上がらない） */
.bulk-action-bar {
  position: sticky;
  top: calc(
    var(--sticky-app-header, 57px) + var(--sticky-tab-menu, 49px)
  );
  z-index: 15;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--primary-color);
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.bulk-count {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
}

.bulk-btn {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-sm);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.bulk-complete {
  background: white;
  color: var(--primary-color);
}

.bulk-complete:hover {
  background: #f0fdf4;
}

.bulk-delete {
  background: #fef2f2;
  color: #dc2626;
}

.bulk-delete:hover {
  background: #fee2e2;
}

.bulk-cancel {
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.bulk-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* フローティングボタン */
.floating-btn {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-hover) 100%
  );
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.4),
    0 8px 24px rgba(180, 207, 158, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  z-index: 100;
}

.floating-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(180, 207, 158, 0.5),
    0 12px 32px rgba(180, 207, 158, 0.3);
}

.floating-btn:active {
  transform: scale(1.05);
}

.floating-btn-icon {
  font-size: 1.25rem;
  line-height: 1;
  color: #000;
}

.floating-btn-icon img {
  filter: brightness(0);
}

@media (max-width: 768px) {
  .checklist-section,
  .checklist-content {
    width: 100%;
  }

  .filter-bar {
    flex-direction: column;
    gap: 0.75rem;
  }

  .filter-group {
    width: 100%;
  }

  .filter-group-date {
    min-width: unset;
  }

  .date-range-inputs input[type="date"] {
    width: 100%;
    flex: 1;
  }

  .floating-btn {
    bottom: 1rem;
    right: 1rem;
    width: 3.5rem;
    height: 3.5rem;
  }

  .floating-btn-icon {
    font-size: 1.5rem;
  }
}

/* デスクトップ: サイドバー非表示、フィルター常時表示 */
@media (min-width: 769px) {
  .filter-toggle-btn {
    display: none;
  }

  .filter-overlay {
    display: none !important;
  }

  .filter-sidebar {
    position: static;
    width: auto;
    height: auto;
    box-shadow: none;
    display: block;
    background: #f9fafb;
    border-radius: 12px;
    margin-bottom: 1.5rem;
  }

  .filter-sidebar-header {
    display: none;
  }

  .filter-bar {
    align-items: flex-end;
    background: transparent;
  }
}

.template-copy-btn {
  margin-top: 0.75rem;
  padding: 0.6rem 1.25rem;
  border: 1.5px dashed var(--primary-color);
  border-radius: 8px;
  background: transparent;
  color: var(--primary-color);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.template-copy-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
}

.template-copy-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
